import type {
  EmploymentTypeInstance,
  EmploymentTypeValue,
} from '@/types/employmentType';

export type EmploymentTypeFieldFormValues = Record<
  string,
  Pick<EmploymentTypeValue, 'numeric_value' | 'text_value'>
>;

export type EmploymentTypeFormValues = {
  type?: { id?: string };
  values?: Record<string, { field?: EmploymentTypeFieldFormValues }>;
};

type EmploymentTypeHtmlFormValues = Record<string, string | number>;

export const mapEmploymentTypeFormToHtmlForm = (
  formData: EmploymentTypeFormValues | undefined,
): EmploymentTypeHtmlFormValues => {
  if (!formData?.type?.id) {
    return { [`type.id`]: '' };
  }

  const typeValues = formData.values?.[formData.type.id];
  if (!typeValues?.field) {
    return { [`type.id`]: formData.type.id };
  }

  return {
    [`type.id`]: formData.type.id,
    ...Object.entries(typeValues.field).reduce<EmploymentTypeHtmlFormValues>(
      (acc, [key, value], idx) => ({
        ...acc,
        [`values[${idx}]field.id`]: key,
        [`values[${idx}]text_value`]: value.text_value || '',
        [`values[${idx}]numeric_value`]: value.numeric_value || 0,
      }),
      {},
    ),
  };
};

export const mapEmploymentTypeFormToApi = (
  formData: EmploymentTypeFormValues | undefined,
): EmploymentTypeInstance => {
  if (!formData?.type?.id) {
    return {
      type: formData?.type?.id ? { id: formData.type.id } : { id: '' },
      values: [],
    };
  }

  const typeValues = formData.values?.[formData.type.id];
  if (!typeValues?.field) {
    console.warn(
      'mapEmploymentTypeFormToApi: No values found for employment type',
      formData.type.id,
    );
    return {
      type: {
        id: formData.type.id,
      },
      values: [],
    };
  }

  return {
    type: {
      id: formData.type.id,
    },
    values: Object.entries(typeValues.field).map(([key, value]) => ({
      field: {
        id: key,
      },
      ...value,
    })),
  };
};

export const mapEmploymentTypeApiToForm = (
  apiData: EmploymentTypeInstance | EmploymentTypeFormValues | undefined,
): EmploymentTypeFormValues => {
  if (!apiData?.type?.id) {
    return {
      ...apiData,
      values: {},
    };
  }

  const instance = apiData as EmploymentTypeInstance;
  if (!Array.isArray(instance.values)) {
    console.warn('mapEmploymentTypeApiToForm: Values is not an array', apiData);
    return {
      ...apiData,
      values: {
        [apiData.type.id]: {
          field: {},
        },
      },
    };
  }

  return {
    ...apiData,
    values: {
      [apiData.type.id]: {
        field: instance.values.reduce<EmploymentTypeFieldFormValues>(
          (acc, entry) => {
            if (!entry?.field?.id) {
              console.warn(
                'mapEmploymentTypeApiToForm: Entry missing field.id',
                entry,
              );
              return acc;
            }
            return {
              ...acc,
              [entry.field.id]: {
                numeric_value: entry.numeric_value,
                text_value: entry.text_value,
              },
            };
          },
          {},
        ),
      },
    },
  };
};
